import type { QueryClient } from '@tanstack/react-query';

type Snapshot<TData> = [queryKey: readonly unknown[], data: TData | undefined][];

export type OptimisticDeleteContext<TData> = {
  snapshots: Snapshot<TData>;
};

/**
 * Cancel in-flight queries, snapshot all matching cache entries,
 * and optimistically update the cache. Returns snapshots for rollback.
 *
 * Uses `setQueriesData` so parameterized variants of the same key
 * (e.g. filters, pagination) are also updated.
 */
export async function optimisticRemove<TData>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  updater: (data: TData) => TData
): Promise<OptimisticDeleteContext<TData>> {
  await queryClient.cancelQueries({ queryKey });

  const snapshots = queryClient.getQueriesData<TData>({ queryKey });

  queryClient.setQueriesData<TData>({ queryKey }, (old) => (old ? updater(old) : old));

  return { snapshots };
}

/**
 * Restore cache entries from snapshots captured by `optimisticRemove`.
 */
export function rollbackOptimisticRemove<TData>(
  queryClient: QueryClient,
  context: OptimisticDeleteContext<TData> | undefined
): void {
  if (!context?.snapshots) return;
  for (const [key, data] of context.snapshots) {
    queryClient.setQueryData(key, data);
  }
}
